import { create } from 'zustand';
import { errorHandler } from '@/utils/functions/errorHandler';
import { getMyKpis } from '@/api/myKpi';

export interface IMyKpi {
  id: number;
  tenant_id: number;
  template_name: string;
  role: {
    id: number;
    name: string;
  };
  period: string;
  start_date: string;
  end_date: string;
  total_kpi: number;
  total_weight: number;
  status: string;
  progress: number | null;
  score: number | null;
  created_by: number;
  created_at: string;
  updated_at: string;
}

interface PaginationParams {
  page?: number;
  limit?: number;
  page_size?: number;
  order?: string;
  order_by?: string;
  search?: string;
  period?: string;
  status?: string;
}

interface PaginatedData {
  content: IMyKpi[];
  totalElements: number;
  totalPages: number;
}

interface MyKpiState {
  myKpis: IMyKpi[];
  paginatedData: PaginatedData | null;
  isLoading: boolean;
  error: unknown;

  getMyKpis: (tenantId: string | number, params?: PaginationParams) => Promise<void>;
  clearMyKpis: () => void;
}

const useMyKpiState = create<MyKpiState>(set => ({
  myKpis: [],
  paginatedData: null,
  isLoading: false,
  error: null,

  clearMyKpis: () => set({ myKpis: [], paginatedData: null }),

  getMyKpis: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getMyKpis(tenantId, params as Record<string, unknown>);
      const raw = response; // getMyKpis directly returns the response body
      const content: IMyKpi[] = raw?.data?.content ?? raw?.data ?? raw?.content ?? [];
      const total: number = raw?.data?.totalElements ?? raw?.totalElements ?? raw?.total ?? content.length;
      const limit = raw?.data?.size ?? raw?.limit ?? params?.limit ?? params?.page_size ?? 10;
      const totalPages: number = raw?.data?.totalPages ?? raw?.totalPages ?? (Math.ceil(total / limit) || 1);

      set({
        myKpis: content,
        paginatedData: {
          content,
          totalElements: total,
          totalPages,
        },
      });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },
}));

export default useMyKpiState;
